import { Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { CanvasSearchResult, getParentPath, type SearchMatch } from './canvas-search-result'

interface CanvasSearchProps {
  data: unknown | null
  externalSearchQuery?: string
}

function formatPath(path: (string | number)[]): string {
  let result = ''
  for (let i = 0; i < path.length; i++) {
    const segment = path[i]
    if (typeof segment === 'number') {
      result += `[${segment}]`
    } else {
      result += i === 0 ? segment : `.${segment}`
    }
  }
  return result
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Recursive search requires this complexity
function searchJSON(
  obj: unknown,
  query: string,
  currentPath: (string | number)[] = [],
  rootObject?: unknown,
): SearchMatch[] {
  const results: SearchMatch[] = []
  const lowerQuery = query.toLowerCase()

  const root = rootObject ?? obj

  if (obj === null || obj === undefined) {
    return results
  }

  if (typeof obj !== 'object') {
    const stringValue = String(obj).toLowerCase()
    if (stringValue.includes(lowerQuery)) {
      results.push({
        path: currentPath,
        fullPath: formatPath(currentPath),
        value: obj,
        matchedObject: obj,
        rootObject: root,
      })
    }
    return results
  }

  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      results.push(...searchJSON(item, query, [...currentPath, index], root))
    })
  } else {
    for (const [key, value] of Object.entries(obj)) {
      if (key === 'children') {
        if (Array.isArray(value)) {
          results.push(...searchJSON(value, query, [...currentPath, key], root))
        }
      } else {
        if (key.toLowerCase().includes(lowerQuery)) {
          results.push({
            path: [...currentPath, key],
            fullPath: formatPath([...currentPath, key]),
            value,
            matchedObject: obj,
            rootObject: root,
          })
        }
        results.push(...searchJSON(value, query, [...currentPath, key], root))
      }
    }
  }

  return results
}

export function CanvasSearch({ data, externalSearchQuery }: CanvasSearchProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const lastExternalQueryRef = useRef<string>('')

  useEffect(() => {
    if (externalSearchQuery && externalSearchQuery !== lastExternalQueryRef.current) {
      lastExternalQueryRef.current = externalSearchQuery
      setSearchQuery(externalSearchQuery)
    }
  }, [externalSearchQuery])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const results = useMemo(() => {
    if (!(data && debouncedQuery.trim())) {
      return []
    }

    const allMatches = searchJSON(data, debouncedQuery.trim()).filter(
      (match) => !match.fullPath.includes('assetRef'),
    )

    const seenParents = new Set<string>()
    const deduplicated: SearchMatch[] = []

    for (const match of allMatches) {
      const parentPath = getParentPath(match.path)
      const parentKey = formatPath(parentPath)

      if (!seenParents.has(parentKey)) {
        seenParents.add(parentKey)
        deduplicated.push(match)
      }
    }

    return deduplicated.slice(0, 100)
  }, [data, debouncedQuery])

  if (!data) {
    return (
      <div className="flex h-full items-center justify-center rounded-md border border-dashed p-8">
        <div className="text-center">
          <Search className="mx-auto mb-2 h-12 w-12 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">No Internal Canvas data to search</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-muted/30 px-4 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search fields and values..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        {debouncedQuery && (
          <div className="mt-2 text-xs text-muted-foreground">
            {results.length > 0 ? (
              <>
                {results.length} {results.length === 1 ? 'match' : 'matches'} found
                {results.length === 100 && ' (showing first 100)'}
              </>
            ) : (
              'No matches found'
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-hidden">
        {!debouncedQuery ? (
          <div className="flex h-full items-center justify-center p-8">
            <p className="text-sm text-muted-foreground">
              Enter a search term to find matching fields
            </p>
          </div>
        ) : results.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8">
            <p className="text-sm text-muted-foreground">No matches found for "{debouncedQuery}"</p>
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="p-4">
              {results.map((result) => (
                <CanvasSearchResult key={result.fullPath} result={result} />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  )
}
